import { useState } from "react";
import { Smartphone } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Logo";

const AUTH_HERO = "https://images.pexels.com/photos/1233281/pexels-photo-1233281.jpeg?auto=compress&cs=tinysrgb&w=1200";

function AuthShell({ children }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:block relative">
        <img src={AUTH_HERO} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-olive-dark/60" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <div className="flex items-center gap-2 mb-3"><Logo size={44} /><span className="font-serif text-2xl">L’Orne Park</span></div>
          <p className="font-serif text-3xl leading-tight">Réservez, échangez, gérez votre équipe — au même endroit.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md fade-up">{children}</div>
      </div>
    </div>
  );
}

export function GoogleButton() {
  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/mes-sejours";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <Button variant="outline" onClick={handleGoogle} className="w-full h-11 border-border" data-testid="google-auth-button">
      <img src="https://www.google.com/favicon.ico" alt="" className="w-4 h-4 mr-2" /> Continuer avec Google
    </Button>
  );
}

export default function Login() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data.user);
      navigate(data.user.role === "admin" ? "/administration" : data.user.role === "host" ? "/espace-hote" : "/mes-sejours");
    } catch (e2) {
      setError(formatApiError(e2.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <AuthShell>
      <h1 className="font-serif text-4xl text-olive">Bon retour</h1>
      <p className="text-muted-foreground mt-1 mb-6">Connectez-vous à votre espace</p>
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div>
          <Label>Email</Label>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1 h-11" data-testid="login-email" />
        </div>
        <div>
          <div className="flex items-center justify-between"><Label>Mot de passe</Label><Link to="/mot-de-passe-oublie" className="text-xs text-rose-brand hover:underline" data-testid="forgot-link">Mot de passe oublié ?</Link></div>
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1 h-11" data-testid="login-password" />
        </div>
        {error && <p className="text-sm text-destructive" data-testid="login-error">{error}</p>}
        <Button type="submit" disabled={busy} className="w-full h-11 bg-rose-brand hover:bg-rosebrand-soft text-white" data-testid="login-submit">
          {busy ? "Connexion..." : "Se connecter"}
        </Button>
      </form>
      <div className="flex items-center gap-3 my-5"><div className="flex-1 h-px bg-border" /><span className="text-xs text-muted-foreground">OU</span><div className="flex-1 h-px bg-border" /></div>
      <Link to="/acces-rapide" className="flex items-center justify-center gap-2 w-full h-11 rounded-xl border border-olive text-olive text-sm font-medium hover:bg-secondary transition-colors mb-3" data-testid="login-quick-access">
        <Smartphone className="w-4 h-4" /> Client : accès rapide par SMS (sans mot de passe)
      </Link>
      <GoogleButton />
      <p className="text-sm text-center text-muted-foreground mt-6">
        Pas encore de compte ? <Link to="/inscription" className="text-rose-brand font-semibold">Inscrivez-vous</Link>
      </p>
    </AuthShell>
  );
}
